<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Settings Cards -->
      <div class="space-y-6">
        <!-- Company Information Card -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-6 py-5 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center">
              <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {{ t('settings.companyInfo') }}
            </h2>
            <p class="mt-1 text-sm text-gray-500">
              {{ t('settings.companyInfoDesc') }}
            </p>
          </div>
          <div class="px-6 py-5">
            <div class="max-w-2xl space-y-6">

              <!-- Logo upload -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-3">{{ t('settings.companyLogo') }}</label>
                <div class="flex items-start gap-6">
                  <!-- Preview box -->
                  <div class="flex-shrink-0 w-40 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                    <img
                      v-if="companyLogoUrl"
                      :src="companyLogoUrl"
                      alt="Company logo"
                      class="max-w-full max-h-full object-contain p-1"
                    />
                    <div v-else class="text-center">
                      <svg class="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p class="text-xs text-gray-400 mt-1">{{ t('settings.noLogo') }}</p>
                    </div>
                  </div>
                  <!-- Upload controls -->
                  <div class="flex-1 space-y-2">
                    <input
                      ref="logoFileInput"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      class="hidden"
                      @change="handleLogoFileChange"
                    />
                    <button
                      @click="$refs.logoFileInput.click()"
                      :disabled="isUploadingLogo"
                      class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <svg class="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      {{ isUploadingLogo ? t('settings.uploading') : t('settings.uploadLogo') }}
                    </button>
                    <button
                      v-if="companyLogoUrl"
                      @click="removeLogo"
                      :disabled="isUploadingLogo"
                      class="ml-2 inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {{ t('settings.removeLogo') }}
                    </button>
                    <p class="text-xs text-gray-400">{{ t('settings.logoHint') }}</p>
                  </div>
                </div>
                <div v-if="logoErrorMessage" class="mt-2 text-sm text-red-600">{{ logoErrorMessage }}</div>
              </div>

              <!-- Company Name -->
              <div>
                <label for="companyName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('settings.companyName') }} <span class="text-red-500">*</span>
                </label>
                <input
                  id="companyName"
                  v-model="companyName"
                  type="text"
                  maxlength="100"
                  :placeholder="t('settings.companyNamePlaceholder')"
                  :disabled="isSavingCompany"
                  class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <p class="mt-1 text-xs text-gray-400">{{ companyName.length }}/100 characters</p>
              </div>

              <!-- Address -->
              <div>
                <label for="companyAddress" class="block text-sm font-medium text-gray-700 mb-1">{{ t('settings.companyAddress') }}</label>
                <input
                  id="companyAddress"
                  v-model="companyAddress"
                  type="text"
                  maxlength="200"
                  placeholder="e.g. 123 Business St, Amman, Jordan"
                  :disabled="isSavingCompany"
                  class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <!-- Phone & Website side by side -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label for="companyPhone" class="block text-sm font-medium text-gray-700 mb-1">{{ t('settings.companyPhone') }}</label>
                  <input
                    id="companyPhone"
                    v-model="companyPhone"
                    type="text"
                    maxlength="50"
                    placeholder="+962 6 123 4567"
                    :disabled="isSavingCompany"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label for="companyWebsite" class="block text-sm font-medium text-gray-700 mb-1">{{ t('settings.companyWebsite') }}</label>
                  <input
                    id="companyWebsite"
                    v-model="companyWebsite"
                    type="text"
                    maxlength="100"
                    placeholder="www.example.com"
                    :disabled="isSavingCompany"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <!-- Save button -->
              <div>
                <button
                  @click="saveCompanySettings"
                  :disabled="isSavingCompany || !companySettingsChanged"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="isSavingCompany" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isSavingCompany ? t('common.loading') : t('settings.saveCompany') }}
                </button>
              </div>

              <div v-if="showCompanySuccess" class="p-4 bg-green-50 border border-green-200 rounded-md">
                <div class="flex">
                  <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  <p class="ml-3 text-sm font-medium text-green-800">{{ t('settings.settingsSaved') }}</p>
                </div>
              </div>

              <div v-if="companyErrorMessage" class="p-4 bg-red-50 border border-red-200 rounded-md">
                <div class="flex">
                  <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                  <p class="ml-3 text-sm font-medium text-red-800">{{ companyErrorMessage }}</p>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Currency Settings Card -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-6 py-5 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center">
              <svg class="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ t('settings.currencyConfig') }}
            </h2>
            <p class="mt-1 text-sm text-gray-500">
              {{ t('settings.currencyDesc') }}
            </p>
          </div>

          <div class="px-6 py-5">
            <div class="max-w-xl">
              <label for="currency" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('settings.systemCurrency') }}
              </label>
              <select
                id="currency"
                v-model="selectedCurrency"
                @change="handleCurrencyChange"
                :disabled="isSaving"
                class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option v-for="currency in currencies" :key="currency.code" :value="currency.code">
                  {{ currency.symbol }} - {{ currency.name }} ({{ currency.code }})
                </option>
              </select>

              <!-- Preview -->
              <div class="mt-4 p-4 bg-gray-50 rounded-md">
                <p class="text-sm font-medium text-gray-700 mb-2">{{ t('common.preview') }}:</p>
                <div class="space-y-1 text-sm text-gray-600">
                  <p>Salary: {{ formatCurrency(5000) }}</p>
                  <p>Overtime Cost: {{ formatCurrency(125.50) }}</p>
                  <p>Total: {{ formatCurrency(5125.50) }}</p>
                </div>
              </div>

              <!-- Save Button -->
              <div class="mt-4">
                <button
                  @click="saveCurrency"
                  :disabled="isSaving || !hasChanges"
                  class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ isSaving ? t('common.loading') : t('common.save') }}
                </button>
              </div>

              <!-- Success Message -->
              <div v-if="showSuccess" class="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <div class="flex">
                  <svg class="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  <p class="ml-3 text-sm font-medium text-green-800">{{ t('settings.currencyUpdated') }}</p>
                </div>
              </div>

              <!-- Error Message -->
              <div v-if="errorMessage" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <div class="flex">
                  <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                  <p class="ml-3 text-sm font-medium text-red-800">{{ errorMessage }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Settings Cards (Placeholder for future settings) -->
        <div class="bg-white shadow rounded-lg">
          <div class="px-6 py-5 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900 flex items-center">
              <svg class="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {{ t('settings.additionalSettings') }}
            </h2>
            <p class="mt-1 text-sm text-gray-500">
              {{ t('settings.additionalSettingsDesc') }}
            </p>
          </div>
          <div class="px-6 py-5">
            <p class="text-sm text-gray-500">
              {{ t('settings.additionalSettingsHint') }}
            </p>
            <div class="mt-4 flex flex-wrap gap-4">
              <router-link
                to="/admin/working-hours-config"
                class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {{ t('nav.workingHours') }} →
              </router-link>
              <router-link
                to="/admin/auto-approval-settings"
                class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {{ t('settings.autoApproval') }} →
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency';
import { api } from '@/services/api';

const { t } = useI18n();
const {
  currentCurrency,
  getSupportedCurrencies,
  formatCurrency,
  loadCurrency,
  updateCurrency
} = useCurrency();

// ── Currency state ──────────────────────────────────────────────────────────
const selectedCurrency = ref('USD');
const originalCurrency = ref('USD');
const isSaving = ref(false);
const showSuccess = ref(false);
const errorMessage = ref('');

const currencies = computed(() => getSupportedCurrencies());
const hasChanges = computed(() => selectedCurrency.value !== originalCurrency.value);

// ── Company settings state ───────────────────────────────────────────────────
const companyName     = ref('');
const companyAddress  = ref('');
const companyPhone    = ref('');
const companyWebsite  = ref('');
const companyLogoUrl  = ref(null);

const originalCompany = ref({ name: '', address: '', phone: '', website: '' });

const isSavingCompany   = ref(false);
const showCompanySuccess = ref(false);
const companyErrorMessage = ref('');

const isUploadingLogo = ref(false);
const logoErrorMessage = ref('');

const companySettingsChanged = computed(() => {
  const o = originalCompany.value;
  return (
    companyName.value.trim()    !== o.name    ||
    companyAddress.value.trim() !== o.address ||
    companyPhone.value.trim()   !== o.phone   ||
    companyWebsite.value.trim() !== o.website
  ) && companyName.value.trim().length > 0;
});

async function loadCompanySettings() {
  try {
    const res = await api.get('/admin/settings/company');
    const d = res.data?.data || {};
    companyName.value    = d.companyName    || 'ExoTrack';
    companyAddress.value = d.companyAddress || '';
    companyPhone.value   = d.companyPhone   || '';
    companyWebsite.value = d.companyWebsite || '';
    companyLogoUrl.value = d.companyLogoUrl || null;
    originalCompany.value = {
      name:    companyName.value,
      address: companyAddress.value,
      phone:   companyPhone.value,
      website: companyWebsite.value,
    };
  } catch {
    companyName.value = 'ExoTrack';
  }
}

async function saveCompanySettings() {
  if (!companySettingsChanged.value) return;
  isSavingCompany.value = true;
  showCompanySuccess.value = false;
  companyErrorMessage.value = '';
  try {
    await api.put('/admin/settings/company', {
      companyName:    companyName.value.trim(),
      companyAddress: companyAddress.value.trim(),
      companyPhone:   companyPhone.value.trim(),
      companyWebsite: companyWebsite.value.trim(),
    });
    originalCompany.value = {
      name:    companyName.value.trim(),
      address: companyAddress.value.trim(),
      phone:   companyPhone.value.trim(),
      website: companyWebsite.value.trim(),
    };
    showCompanySuccess.value = true;
    setTimeout(() => { showCompanySuccess.value = false; }, 5000);
  } catch (err) {
    companyErrorMessage.value = err.response?.data?.error || 'Failed to update company settings';
  } finally {
    isSavingCompany.value = false;
  }
}

async function handleLogoFileChange(event) {
  const file = event.target.files[0];
  if (!file) return;

  logoErrorMessage.value = '';

  if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
    logoErrorMessage.value = 'Only PNG or JPG images are allowed';
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    logoErrorMessage.value = 'Logo must be smaller than 2 MB';
    return;
  }

  isUploadingLogo.value = true;
  try {
    const form = new FormData();
    form.append('logo', file);
    const res = await api.post('/admin/settings/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    companyLogoUrl.value = res.data?.data?.companyLogoUrl || null;
  } catch (err) {
    logoErrorMessage.value = err.response?.data?.error || 'Failed to upload logo';
  } finally {
    isUploadingLogo.value = false;
    event.target.value = '';
  }
}

async function removeLogo() {
  logoErrorMessage.value = '';
  isUploadingLogo.value = true;
  try {
    await api.delete('/admin/settings/logo');
    companyLogoUrl.value = null;
  } catch (err) {
    logoErrorMessage.value = err.response?.data?.error || 'Failed to remove logo';
  } finally {
    isUploadingLogo.value = false;
  }
}

// Load everything on mount
onMounted(async () => {
  await Promise.all([loadCurrency(), loadCompanySettings()]);
  selectedCurrency.value = currentCurrency.value;
  originalCurrency.value = currentCurrency.value;
});

// Handle currency selection change
const handleCurrencyChange = () => {
  showSuccess.value = false;
  errorMessage.value = '';
};

// Save currency settings
const saveCurrency = async () => {
  if (!hasChanges.value) return;

  isSaving.value = true;
  showSuccess.value = false;
  errorMessage.value = '';

  const result = await updateCurrency(selectedCurrency.value);

  if (result.success) {
    originalCurrency.value = selectedCurrency.value;
    showSuccess.value = true;
    setTimeout(() => {
      showSuccess.value = false;
    }, 5000);
  } else {
    errorMessage.value = result.error || 'Failed to update currency';
  }

  isSaving.value = false;
};
</script>
